import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { queryClient } from '@/app/query-client'
import { authApi, type AuthResponse, type LoginMode } from '@/lib/api'
import {
  clearStoredSession, onSessionExpired, persistOidcSession, persistSession, readStoredSession, refreshOidcSession, setOidcMode,
  setSessionRole, updateStoredProfile, type SessionRole,
} from '@/lib/api-client'
import { demoProfile, schoolDemoProfile } from '@/lib/demo-profile'
import type { UserProfile } from '@/lib/types'
import { reconcileRelease, setReleaseAudience } from '@/lib/release-channel'
import { currentAuthConfig, leaveTo, loadAuthConfig } from '@/features/auth/auth-config'
import { fetchOidcSession, oidcLogout, requestViewAsExit, type Impersonation } from '@/features/auth/oidc-session'
import { announceViewAs } from '@/lib/view-as-sync'

/**
 * What a confirmed profile write may change in the live session.
 *
 * Deliberately a subset of `UserProfile` rather than the full serialized
 * Student: the extra columns the endpoint returns (`country`, `target`,
 * `calling_code`, …) still ride through the spread at runtime and reach
 * storage, but nothing in the auth layer has to know about them, so adding a
 * column to the Django serializer cannot break this contract.
 */
export interface ProfileUpdateFields extends Partial<Omit<UserProfile, 'user'>> {
  user?: Partial<UserProfile['user']>
}

/**
 * Shown on the sign-in page after the server ended a session under the
 * student. Consumer language: it says what happened and what to do, and never
 * mentions tokens, expiry or status codes.
 */
export const SESSION_ENDED_NOTICE = 'Your session ended. Please sign in again.'

interface AuthState {
  profile: UserProfile | null
  loading: boolean
  demo: boolean
  /**
   * Set when the session ended on its own rather than by the student pressing
   * sign out. The sign-in page reads it; `login` clears it.
   */
  sessionNotice: string | null
  login: (email: string, password: string, mode?: LoginMode) => Promise<UserProfile>
  /** Google sign-in. Establishes exactly the same session as `login`. */
  loginWithGoogle: (idToken: string) => Promise<UserProfile>
  /**
   * Merge a server-confirmed profile change into the live session. Called
   * after `PUT /auth/profile/update/` succeeds so the header, the avatar and a
   * later reload all agree with what was saved.
   */
  applyProfileUpdate: (fields: ProfileUpdateFields) => void
  enterDemo: () => void
  /** The sample school viewer: demo builds only, throws otherwise. Ends the student demo; sign-out clears it. */
  enterSchoolDemo: () => void
  logout: () => Promise<void>
  /** Set while a superadmin uses this product as someone else (VIEW-AS-SPEC 2.6); null otherwise. */
  impersonation: Impersonation | null
  /** True only on a superadmin's own, normal Edvantage ID session: the "View as" links are offered then. */
  canViewAs: boolean
  /** End View as: the API revokes the session, then the browser goes to the person's page in Edvantage ID (else to `/`). */
  exitViewAs: () => Promise<void>
  /** Ask the API how sign-in works and who is signed in, again. The sign-in page offers it when the first ask failed. */
  retryBoot: () => void
}

const AuthContext = createContext<AuthState | null>(null)
const DEMO_KEY = 'ielts-v2-demo-session'
// The `ielts-v2-` prefix means `clearV2SessionData` ends it on every sign-out and session expiry.
const SCHOOL_DEMO_KEY = 'ielts-v2-school-demo'
const DEMO_ENABLED = import.meta.env.VITE_ENABLE_DEMO_SESSION === 'true'

/** The backend knows three roles. `school_demo` exists only in the browser (demo builds), so it can never reach these calls. */
function serverRole(role: UserProfile['user']['role']): SessionRole {
  return role === 'admin' || role === 'teacher' ? role : 'student'
}

/** Only staff admins are offered the preview build; a teacher or a candidate always gets production. */
function audienceFor(role: UserProfile['user']['role']): 'admin' | 'student' {
  return role === 'admin' ? 'admin' : 'student'
}

const NO_VIEW_AS = { impersonation: null, canViewAs: false } as const

/** Which session a profile came from: a password session kept in storage (`legacy`), or the Edvantage ID cookies (`oidc`). */
type SessionKind = 'legacy' | 'oidc'

/**
 * Cookie-only (no token or profile in storage, no Bearer) follows the SESSION, not only the product mode. In `both` mode the
 * product mode is not `oidc`, yet an Edvantage ID session there must still be cookie-only: otherwise a mid-session refresh
 * stores its refresh JWT, a profile save stores the profile, and the next boot restores it as a password session that signs
 * out without ending the Edvantage ID session. With no session, it is the product mode.
 */
function holdsOnlyCookies(kind: SessionKind | null) {
  return kind === 'oidc' || currentAuthConfig()?.mode === 'oidc'
}

function clearV2SessionData() {
  for (let index = sessionStorage.length - 1; index >= 0; index -= 1) {
    const key = sessionStorage.key(index)
    if (key?.startsWith('ielts-v2-')) sessionStorage.removeItem(key)
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [profile, setProfile] = useState<UserProfile | null>(null)
  const [loading, setLoading] = useState(true)
  const [demo, setDemo] = useState(false)
  const [sessionNotice, setSessionNotice] = useState<string | null>(null)
  const [viewAs, setViewAs] = useState<{ impersonation: Impersonation | null; canViewAs: boolean }>(NO_VIEW_AS)

  /**
   * The server ended this session. Clear everything the signed-in app is
   * holding, in one place, so nothing is left polling.
   *
   * Setting `profile` to null is what stops the loops: `RequireAuth` redirects
   * to the sign-in page, and `RealtimeBridge`'s effect tears its socket down
   * rather than reconnecting forever. `queryClient.clear()` drops every cached
   * student query along with its refetch interval.
   */
  useEffect(
    () =>
      onSessionExpired(() => {
        sessionStorage.removeItem(DEMO_KEY)
        sessionStorage.removeItem(SCHOOL_DEMO_KEY)
        clearV2SessionData()
        clearStoredSession()
        setOidcMode(holdsOnlyCookies(null))
        setReleaseAudience('public')
        queryClient.clear()
        setProfile(null)
        setViewAs(NO_VIEW_AS)
        setDemo(false)
        setSessionNotice(SESSION_ENDED_NOTICE)
      }),
    [],
  )

  /** Which kind of session `profile` came from. A ref: it is read by sign-out, and never drives a render. */
  const sessionKind = useRef<SessionKind>('legacy')

  /**
   * One place where an Edvantage ID session becomes the signed-in state. The router reads `profile.user.role`; the release
   * audience stays `admin` only for staff admins, so a teacher or a candidate always gets the production build.
   */
  const establishOidcSession = useCallback((role: SessionRole, sessionProfile: UserProfile, viewAsState: { impersonation: Impersonation | null; canViewAs: boolean } = NO_VIEW_AS) => {
    sessionKind.current = 'oidc'
    setOidcMode(holdsOnlyCookies('oidc'))
    persistOidcSession(role)
    setProfile(sessionProfile)
    setViewAs(viewAsState)
    setReleaseAudience(audienceFor(role))
    if (role === 'admin') void reconcileRelease()
    setDemo(false)
    setSessionNotice(null)
  }, [])

  /**
   * Who is signed in through Edvantage ID: the cookies are the session, so ask the API. An expired access cookie is not a
   * sign-out (the refresh cookie lasts longer), so a signed-out answer gets one refresh attempt, but only on a browser that
   * was signed in here before (`refreshOidcSession` says so without a request when no role is remembered).
   * `unavailable` leaves the person on the sign-in page rather than guessing.
   */
  const bootOidc = useCallback(async (isCancelled: () => boolean) => {
    let result = await fetchOidcSession()
    if (result.kind === 'signed-out' && (await refreshOidcSession()) === 'refreshed') result = await fetchOidcSession()
    if (isCancelled()) return
    if (result.kind === 'session') {
      establishOidcSession(result.role, result.profile, { impersonation: result.impersonation, canViewAs: result.canViewAs })
    } else {
      clearStoredSession()
      setProfile(null)
      setViewAs(NO_VIEW_AS)
    }
    setLoading(false)
  }, [establishOidcSession])

  const boot = useCallback(async (isCancelled: () => boolean) => {
    if (DEMO_ENABLED && sessionStorage.getItem(DEMO_KEY) === 'true') {
      setProfile(demoProfile)
      setDemo(true)
      setLoading(false)
      return
    }
    if (DEMO_ENABLED && sessionStorage.getItem(SCHOOL_DEMO_KEY) === 'true') {
      setProfile(schoolDemoProfile)
      setDemo(true)
      setLoading(false)
      return
    }

    // A stale preview session must never bypass live authentication after the
    // environment has been switched to live-backend acceptance testing.
    sessionStorage.removeItem(DEMO_KEY)
    sessionStorage.removeItem(SCHOOL_DEMO_KEY)

    // How does this product sign people in? Asked before anything is restored, so a stored password session is never
    // shown for a moment after the API has moved to Edvantage ID. If the answer cannot be had, the stored session is
    // restored as it always was: the API calls that follow will say whether it still holds.
    const config = await loadAuthConfig()
    if (isCancelled()) return
    const mode = config?.mode ?? 'legacy'
    setOidcMode(mode === 'oidc')

    if (mode === 'oidc') {
      // Nothing from a password session survives the move: `bootOidc` ends with either an Edvantage ID session (which removes
      // any stored token and profile) or a signed-out state (which clears them).
      await bootOidc(isCancelled)
      return
    }

    // Match the established Vue boot contract. The backend has `/auth/me/`,
    // but it returns only a summary and cannot reconstruct the full profile or
    // role needed by the router. Login persists the authoritative profile.
    const storedProfile = readStoredSession<UserProfile>()
    if (storedProfile) {
      sessionKind.current = 'legacy'
      setSessionRole(serverRole(storedProfile.user.role))
      setProfile(storedProfile)
      setReleaseAudience(audienceFor(storedProfile.user.role))
      // Reconcile restored admin sessions once. Student exams never reload
      // because a different frontend release was staged in the background.
      if (storedProfile.user.role === 'admin') void reconcileRelease()
      setLoading(false)
      return
    }
    // `both`: nobody is signed in with a password, so they may be signed in through Edvantage ID.
    if (mode === 'both') {
      await bootOidc(isCancelled)
      return
    }
    setProfile(null)
    setLoading(false)
  }, [bootOidc])

  useEffect(() => {
    let cancelled = false
    void boot(() => cancelled)
    return () => { cancelled = true }
  }, [boot])

  const retryBoot = useCallback(() => {
    setLoading(true)
    void boot(() => false)
  }, [boot])

  /**
   * One place where a completed sign-in becomes a session. Password and Google
   * sign-in must not drift: a socially-authenticated student has to get the
   * same stored profile, the same session role and the same cleared demo flag,
   * or the router and the WebSocket bridge will disagree about who is here.
   */
  const establishSession = useCallback((response: AuthResponse) => {
    sessionKind.current = 'legacy'
    setViewAs(NO_VIEW_AS)
    setOidcMode(holdsOnlyCookies('legacy'))
    persistSession(response.profile, {
      authToken: response.auth_token,
      refreshToken: response.refreshToken,
    })
    setSessionRole(serverRole(response.profile.user.role))
    setProfile(response.profile)
    setReleaseAudience(audienceFor(response.profile.user.role))
    void reconcileRelease()
    setDemo(false)
    // The student has answered the notice by signing in; it must not follow
    // them into the new session.
    setSessionNotice(null)
    return response.profile
  }, [])

  const login = useCallback(async (email: string, password: string, mode: LoginMode = 'student') => {
    return establishSession(await authApi.login(email, password, mode))
  }, [establishSession])

  const loginWithGoogle = useCallback(async (idToken: string) => {
    return establishSession(await authApi.socialAuth(idToken))
  }, [establishSession])

  const applyProfileUpdate = useCallback((fields: ProfileUpdateFields) => {
    setProfile((current) => {
      if (!current) return current
      // `update_profile` returns the serialized Student, whose nested `user`
      // block is the same object login returned. Merging shallowly and keeping
      // the existing `user` avoids a partial response dropping the role the
      // router depends on.
      const merged = { ...current, ...fields, user: { ...current.user, ...(fields.user ?? {}) } }
      if (!demo) updateStoredProfile(merged)
      return merged
    })
  }, [demo])

  const enterDemo = useCallback(() => {
    if (!DEMO_ENABLED) throw new Error('Demo sessions are disabled in this environment.')
    sessionStorage.removeItem(SCHOOL_DEMO_KEY)
    sessionStorage.setItem(DEMO_KEY, 'true')
    setProfile(demoProfile)
    setDemo(true)
  }, [])

  const enterSchoolDemo = useCallback(() => {
    if (!DEMO_ENABLED) throw new Error('Demo sessions are disabled in this environment.')
    sessionStorage.removeItem(DEMO_KEY)
    sessionStorage.setItem(SCHOOL_DEMO_KEY, 'true')
    setProfile(schoolDemoProfile)
    setDemo(true)
  }, [])

  /**
   * Signing out always succeeds from the student's point of view.
   *
   * The local session is torn down unconditionally; the server call is asked
   * for (`void`, not awaited) rather than waited on. Only the server can expire
   * the HttpOnly session cookies, so the request still goes out - but its
   * outcome must not decide whether the student gets signed out. Awaiting it
   * meant a request that never answers (a phone that has dropped off the
   * network holds the connection open instead of failing) left the student
   * signed in, with a sign-out control that looked broken (IELTS-506). Every
   * header is built before the first await inside `apiRequest`, so clearing
   * storage on the next line cannot strip the credentials off the call already
   * in flight, and the server half is now idempotent, so the call succeeds even
   * when the session was already dead.
   */
  const logout = useCallback(async () => {
    // Edvantage ID: the API revokes the refresh token and names the Edvantage ID page that ends that session. The same
    // rules hold: it is asked for, not waited on.
    const ended: Promise<string | null> = demo
      ? Promise.resolve(null)
      : sessionKind.current === 'oidc'
        ? oidcLogout(profile ? serverRole(profile.user.role) : 'student', currentAuthConfig())
        : authApi.logout(profile?.user.role === 'admin').then(() => null)
    sessionStorage.removeItem(DEMO_KEY)
    sessionStorage.removeItem(SCHOOL_DEMO_KEY)
    clearV2SessionData()
    clearStoredSession()
    setOidcMode(holdsOnlyCookies(null))
    queryClient.clear()
    setProfile(null)
    setViewAs(NO_VIEW_AS)
    setDemo(false)
    // A deliberate sign-out is not a session that ended under them.
    setSessionNotice(null)
    const generation = setReleaseAudience('public')
    // Local logout remains immediate; only the build switch waits for the
    // server cookie response. A newer login invalidates this reconciliation.
    void ended.then((redirect) => {
      if (redirect) leaveTo.go(redirect)
      else return reconcileRelease(generation)
    }).catch(() => undefined)
  }, [demo, profile])

  /**
   * Exit View as, by the button or at the end time. Nothing is torn down here: the page is about to leave, and tearing the
   * session down first would only flash the sign-in screen. The API clears this product's cookies; Edvantage ID is where the
   * superadmin lands (their own session there never changed). A failed call still leaves, to `/`.
   */
  const exitViewAs = useCallback(async () => {
    const redirect = await requestViewAsExit(currentAuthConfig())
    announceViewAs('0') // the other tabs of this browser now hold nobody's View as
    leaveTo.go(redirect ?? '/')
  }, [])

  const value = useMemo(
    () => ({ profile, loading, demo, sessionNotice, login, loginWithGoogle, applyProfileUpdate, enterDemo, enterSchoolDemo, logout, retryBoot, impersonation: viewAs.impersonation, canViewAs: viewAs.canViewAs, exitViewAs }),
    [profile, loading, demo, sessionNotice, login, loginWithGoogle, applyProfileUpdate, enterDemo, enterSchoolDemo, logout, retryBoot, viewAs, exitViewAs],
  )
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const value = useContext(AuthContext)
  if (!value) throw new Error('useAuth must be used inside AuthProvider')
  return value
}
